import { useEffect, useState } from "react";
import { track } from "../lib/analytics";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true)) || false;

// Reads the install prompt captured globally in index.js (which may have fired
// before React mounted) and keeps in sync via custom events.
export function usePwaInstall() {
  const [promptEvent, setPromptEvent] = useState(() =>
    typeof window !== "undefined" ? window.__deferredInstallPrompt : null
  );
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    // Pick up an event that may already have fired before this component mounted.
    if (window.__deferredInstallPrompt) setPromptEvent(window.__deferredInstallPrompt);

    const onInstallable = () => setPromptEvent(window.__deferredInstallPrompt);
    const onPrompt = (e) => { e.preventDefault(); window.__deferredInstallPrompt = e; setPromptEvent(e); };
    const onInstalled = () => { setInstalled(true); setPromptEvent(null); window.__deferredInstallPrompt = null; track("install"); };

    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Returns { outcome: "accepted" | "dismissed" | "unavailable" } so callers can
  // fall back to a manual "Add to Home Screen" hint when no native prompt exists.
  const install = async () => {
    const evt = promptEvent || window.__deferredInstallPrompt;
    if (!evt) return { outcome: "unavailable" };
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      window.__deferredInstallPrompt = null;
      setPromptEvent(null);
      return { outcome: choice?.outcome || "dismissed" };
    } catch (e) {
      window.__deferredInstallPrompt = null;
      setPromptEvent(null);
      return { outcome: "unavailable" };
    }
  };

  return { canInstall: !!promptEvent && !installed, installed, install };
}
